import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Alert, Box, Button, Checkbox, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, Divider, IconButton, List, ListItem,
  ListItemButton, ListItemIcon, ListItemText, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';

// Modal for previewing and sending the weekly DO facilities email.
// Two-pane layout: list of DOs (with per-DO checkbox) on the left, iframe
// showing the exact HTML that DO would receive on the right.
export default function DoEmailDialog({ open, onClose, firebaseUser, getToken }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dos, setDos] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(() => new Set());
  const [activeEmail, setActiveEmail] = useState(null);
  const [replyTo, setReplyTo] = useState('');
  const [sending, setSending] = useState(false);
  const [sendResults, setSendResults] = useState(null);

  const activeDo = useMemo(
    () => dos.find((d) => d.dirEmail === activeEmail) || dos[0] || null,
    [dos, activeEmail]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setSendResults(null);
    try {
      const token = await getToken();
      const { data } = await axios.get('/hd-api/facilities-review/do-email-preview', {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = data?.dos || [];
      setDos(list);
      setSelectedEmails(new Set(list.map((d) => d.dirEmail)));
      setActiveEmail(list[0]?.dirEmail || null);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => {
    if (!open) return;
    setReplyTo(firebaseUser?.email || '');
    load();
  }, [open, load, firebaseUser]);

  const toggle = (email) => {
    const next = new Set(selectedEmails);
    if (next.has(email)) next.delete(email);
    else next.add(email);
    setSelectedEmails(next);
  };

  const toggleAll = () => {
    if (selectedEmails.size === dos.length) setSelectedEmails(new Set());
    else setSelectedEmails(new Set(dos.map((d) => d.dirEmail)));
  };

  const handleSend = async () => {
    if (!selectedEmails.size || !replyTo) return;
    setSending(true);
    setError(null);
    try {
      const token = await getToken();
      const { data } = await axios.post(
        '/hd-api/facilities-review/do-email-send',
        { dirEmails: Array.from(selectedEmails), replyTo },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSendResults(data?.results || []);
    } catch (e) {
      setError(e.response?.data?.error || e.message || 'Send failed');
    } finally {
      setSending(false);
    }
  };

  const errorCount = (sendResults || []).filter((r) => r.status !== 'sent').length;
  const successCount = (sendResults || []).filter((r) => r.status === 'sent').length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <span>Send Weekly DO Emails</span>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0, height: 640 }}>
        {loading && (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
        )}
        {error && !loading && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {!loading && !error && dos.length === 0 && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No DOs found with stores + email on file.</Typography>
          </Box>
        )}

        {!loading && !error && dos.length > 0 && (
          <Box sx={{ display: 'flex', height: '100%' }}>
            <Box sx={{ width: 320, borderRight: '1px solid', borderColor: 'divider', overflow: 'auto' }}>
              <List dense disablePadding>
                <ListItem sx={{ bgcolor: 'grey.50' }}>
                  <ListItemIcon sx={{ minWidth: 36 }}>
                    <Checkbox
                      edge="start"
                      size="small"
                      checked={selectedEmails.size === dos.length}
                      indeterminate={selectedEmails.size > 0 && selectedEmails.size < dos.length}
                      onChange={toggleAll}
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={`${selectedEmails.size} of ${dos.length} selected`}
                    primaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
                  />
                </ListItem>
                <Divider />
                {dos.map((d) => (
                  <ListItem key={d.dirEmail} disablePadding sx={{
                    bgcolor: activeDo?.dirEmail === d.dirEmail ? 'action.selected' : undefined,
                  }}>
                    <ListItemIcon sx={{ minWidth: 36, pl: 2 }}>
                      <Checkbox
                        edge="start"
                        size="small"
                        checked={selectedEmails.has(d.dirEmail)}
                        onChange={() => toggle(d.dirEmail)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </ListItemIcon>
                    <ListItemButton onClick={() => setActiveEmail(d.dirEmail)}>
                      <ListItemText
                        primary={d.dirOps || d.dirEmail}
                        secondary={`${d.stores.length} store${d.stores.length === 1 ? '' : 's'} · ${d.dirEmail}`}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>

            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
              <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" color="text.secondary">To</Typography>
                <Typography variant="body2">{activeDo?.dirEmail}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>Subject</Typography>
                <Typography variant="body2" fontWeight={500}>{activeDo?.subject}</Typography>
              </Box>
              <Box sx={{ flex: 1, overflow: 'hidden' }}>
                {activeDo && (
                  <iframe
                    title="email preview"
                    srcDoc={activeDo.htmlBody}
                    style={{ border: 0, width: '100%', height: '100%' }}
                    sandbox=""
                  />
                )}
              </Box>
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Reply-to"
          size="small"
          value={replyTo}
          onChange={(e) => setReplyTo(e.target.value)}
          sx={{ minWidth: 280 }}
          disabled={sending}
        />
        <Box sx={{ flex: 1 }} />
        {sendResults && (
          <Alert
            severity={errorCount ? 'warning' : 'success'}
            sx={{ py: 0, px: 1 }}
          >
            {successCount} sent{errorCount ? ` · ${errorCount} failed` : ''}
          </Alert>
        )}
        <Button onClick={onClose} disabled={sending}>Close</Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={sending || loading || !selectedEmails.size || !replyTo}
          startIcon={sending ? <CircularProgress size={14} /> : <SendIcon />}
        >
          Send to {selectedEmails.size} DO{selectedEmails.size === 1 ? '' : 's'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
